<template>
  <div id="meeting_intelligence" class="flex flex-col gap-6">
    <div
      id="meeting_intelligence_header"
      class="flex flex-row justify-between items-center"
    >
      <h1 class="text-xl font-semibold text_color">Meeting Intelligence</h1>
    </div>

    <SkeletonLoader v-if="fetching" :page="loaderPages.MEETING_INTELLIGENCE" />

    <div v-else class="flex flex-col gap-6">
      <div id="voice_conferencing_apps" class="flex flex-col gap-6">
        <span class="text-lg">Video conferencing apps</span>

        <div class="flex flex-col gap-1">
          <span class="text-sm primary_color"
            >Record via SuperAGI Recorder</span
          >
          <span class="text-[13px] secondary_color"
            >Based on your auto-record settings, the recorder will join meetings
            with links in your calendar events from the enabled conferencing
            apps.</span
          >
        </div>

        <ConferencingApps
          source="meeting_intelligence"
          v-model="conferencingApps"
        />
      </div>

      <div id="recorder_settings" class="flex flex-col gap-6 w-1/2">
        <span class="text-lg">Settings</span>

        <div class="flex flex-col gap-1">
          <span class="text-[13px] secondary_color"
            >Custom name for your recorder</span
          >
          <a-input
            class="input_medium"
            v-model:value="recorderName"
            placeholder="Enter recorder name"
            @focusout="toUpdateRecorderSetting()"
          />
        </div>

        <div class="flex items-start gap-3">
          <a-switch
            v-model:checked="autoCreateTasks"
            size="small"
            @change="handleAutoCreateTasksToggle"
          />
          <div class="flex flex-col gap-1">
            <span class="text-sm text_color font-medium">Create tasks automatically</span>
            <span class="text-[13px] secondary_color">
              Let SuperAGI create tasks automatically from the meeting insights
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Notetaker Modal -->
    <AddNotetakerModal v-model:visible="addNotetakerModalVisible" />
  </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import { useStore } from "vuex";
import { message } from "ant-design-vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import {
  meetingRecorderAppsEnabledMapping,
  meetingRecorderAppsIconMapping,
} from "@/src/constants/conversationIntelligenceConstants.js";
import DashboardService from "@/src/api/DashboardService.js";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import AddNotetakerModal from "@/src/components/meetings_components/AddNotetakerModal.vue";
import { checkNotetakerInstalled } from "@/src/utils/appStoreUtils.js";
import ConferencingApps from "@/src/views/meetings/conversational_intelligence/intelligence/ConferencingApps.vue";

const fetching = ref(false);
const conferencingApps = ref(null);
const recorderName = ref("SuperAGI Notetaker");
const autoCreateTasks = ref(false);

const addNotetakerModalVisible = ref(false);
const notetakerAppInstalled = ref(false);
const store = useStore();

const toUpdateRecorderSetting = async (autoCreateTasksValue = null) => {
  try {
    const payload = {
      recorder_name: recorderName.value,
    };

    if (autoCreateTasksValue !== null) {
      payload.auto_create_meeting_tasks = autoCreateTasksValue;
    }

    const response = await DashboardService.updateRecorderSetting(payload);
    if (response?.success) {
      if (autoCreateTasksValue !== null) {
        message.success(`Auto create tasks ${autoCreateTasksValue ? "enabled" : "disabled"}`);
      } else {
        message.success("Recorder name successfully updated!");
      }
    }
  } catch (error) {
    console.error("An error occurred while updating recorder settings: ", error);
    if (autoCreateTasksValue !== null) {
      autoCreateTasks.value = !autoCreateTasksValue;
      message.error("Failed to update auto create tasks setting");
    } else {
      message.error("Failed to update recorder name!");
    }
  }
};

const handleAutoCreateTasksToggle = async (checked) => {
  await toUpdateRecorderSetting(checked);
};

const toGetMeetingRecorderSettings = async () => {
  try {
    fetching.value = true;

    // Fetch features to check if zoom_integration_meeting_intelligence is enabled
    const featuresResponse = await DashboardService.fetchEnabledFeatures();
    const zoomMeetingIntelligenceEnabled = featuresResponse?.features?.zoom_integration_meeting_intelligence_enabled || false;

    const response = await DashboardService.getMeetingRecorderSettings();
    if (response?.success) {
      const data = response?.data;

      recorderName.value = data?.recorder_name;
      autoCreateTasks.value = data?.auto_create_meeting_tasks || false;
      conferencingApps.value = data?.conferencing_apps?.map((app) => {
        let isEnabled = meetingRecorderAppsEnabledMapping[app?.id];

        // Override Zoom enabled status based on feature toggle
        if (app?.id === 'zoom') {
          isEnabled = zoomMeetingIntelligenceEnabled;
        }

        return {
          ...app,
          icon: meetingRecorderAppsIconMapping[app?.id],
          is_enabled: isEnabled,
        };
      });
    }
  } catch (error) {
    console.error(
      "An error occurred while fetching meeting intelligence details: ",
      error,
    );
  } finally {
    fetching.value = false;
  }
};

// Add Notetaker Modal methods
const showAddNotetakerModal = () => {
  addNotetakerModalVisible.value = true;
};

onMounted(async () => {
  // Run notetaker check in background so it doesn't block recorder settings fetch
  checkNotetakerInstalled(store)
    .then((installed) => {
      notetakerAppInstalled.value = installed;
    })
    .catch((err) => {
      console.error("Error checking notetaker installation:", err);
    });

  toGetMeetingRecorderSettings();
});
</script>
